import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ConfigGroupInfo, CustomColumn } from '@/common/utils/types';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';

import { MetaAlertRuleItem } from '@/api-client/metav8Over/model';
import { getV9AlertAlertRules } from '@/api-client/metav8Over/queries/meta-alert-rule-v9-controller/meta-alert-rule-v9-controller';

import { useAlertTagsAndTargets, TARGET_TYPE } from '@/alert/utils/tagsAndTargets.uses';
import { AlertRuleListRowData } from '@/config/views/userAlert/userAlertRuleListGrid/userAlertRuleList.type';
import type { RuleCriteria } from '@/alert/components/alertDetail/alertDetail.types';

interface WindowState {
  isShow: boolean;
  isLoading: boolean;
  selectedId: string;
  ruleCriteria?: RuleCriteria;
}
interface CopyWindowState extends WindowState {
  type: 'rule' | 'group';
  name: string;
  groupId?: string | null;
}

interface GroupMoveWindowState extends Omit<WindowState, 'selectedId'> {
  type: 'rule-move';
  names: string[];
  selectedIds: string[];
  groupId: string;
}

export const useAlertRuleList = () => {
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();

  const fetchRuleList = async (groupId: string): Promise<MetaAlertRuleItem[]> => {
    try {
      const {
        data: { data = [] },
      } = await getV9AlertAlertRules(
        {
          alertGroupId: groupId,
        },
        {
          frameName: FRAME_NAMES.CONFIG_USER_ALERT.RULE_LIST,
        },
      );

      return data;
    } catch (error) {
      console.log(error);
      showErrorMsg({ msgStr: t('NOTI.PRODUCT.TEMP_SERVER_ERROR') });
      return [];
    }
  };

  return {
    fetchRuleList,
  };
};

export const useAlertRuleListStore = defineStore('alertRuleListStore', () => {
  const isGridReady = ref(false);

  const ruleRows = ref<AlertRuleListRowData[]>([]);
  const checkedRows = ref<AlertRuleListRowData[]>([]);
  const originData = ref<MetaAlertRuleItem[]>([]);

  const isLoading = ref(false);

  const { fetchRuleList } = useAlertRuleList();
  const { makeAlertRows } = useAlertTagsAndTargets();

  const bulkRuleWindowState = ref<Omit<WindowState, 'selectedId'>>({
    isShow: false,
    isLoading: false,
  });

  const ruleWindowState = ref<WindowState>({
    isShow: false,
    isLoading: false,
    selectedId: '',
  });

  const copyWindowState = ref<CopyWindowState>({
    type: 'rule',
    isShow: false,
    isLoading: false,
    selectedId: '',
    name: '',
    groupId: null,
  });

  const ruleMoveWindowState = ref<GroupMoveWindowState>({
    type: 'rule-move',
    isShow: false,
    isLoading: false,
    selectedIds: [],
    names: [],
    groupId: '',
  });

  const notificationWindowState = ref<WindowState>({
    isShow: false,
    isLoading: false,
    selectedId: '',
    ruleCriteria: undefined,
  });

  const updateRuleRows = (
    resData: MetaAlertRuleItem[],
    columns: CustomColumn[] | readonly CustomColumn[],
  ) => {
    isGridReady.value = true;
    ruleRows.value = makeAlertRows({
      data: resData,
      columns,
      targetFieldName: 'tags',
      targetType: TARGET_TYPE.TAGS,
      formatter: { activation: undefined, mute: (v) => !v },
    });
  };

  const resetCheckedRows = () => {
    checkedRows.value = [];
  };

  const applyRuleData = (
    resData: MetaAlertRuleItem[],
    columns: CustomColumn[] | readonly CustomColumn[],
  ) => {
    originData.value = resData;
    updateRuleRows(resData, columns);
  };

  const getRuleList = async (
    groupId: ConfigGroupInfo['id'],
    columns: CustomColumn[] | readonly CustomColumn[],
  ) => {
    isLoading.value = true;
    resetCheckedRows();

    try {
      const data = await fetchRuleList(groupId);

      applyRuleData(data ?? [], columns);
    } catch (error) {
      console.log(error);
      ruleRows.value = [];
      originData.value = [];
    }

    isLoading.value = false;
  };

  const setRuleWindowState = (isOpen: boolean, ruleId?: string) => {
    ruleWindowState.value = {
      isShow: isOpen,
      isLoading: isOpen,
      selectedId: ruleId ?? '',
    };
  };

  const setBulkRuleWindowState = (isOpen: boolean) => {
    bulkRuleWindowState.value = {
      isShow: isOpen,
      isLoading: isOpen,
    };
  };

  const setCopyWindowState = (
    isOpen: boolean,
    info?: {
      type: CopyWindowState['type'];
      selectedId?: string;
      name?: string;
      groupId?: string;
    },
  ) => {
    copyWindowState.value = {
      type: info?.type ?? copyWindowState.value.type,
      isShow: isOpen,
      isLoading: isOpen,
      selectedId: info?.selectedId ?? '',
      name: info?.name ?? '',
      groupId: info?.groupId ?? null,
    };
  };

  const setRuleMoveWindowState = (
    isOpen: boolean,
    info?: {
      type: GroupMoveWindowState['type'];
      selectedIds: string[];
      names: string[];
      groupId: string;
    },
  ) => {
    ruleMoveWindowState.value = {
      type: info?.type ?? ruleMoveWindowState.value.type,
      isShow: isOpen,
      isLoading: isOpen,
      selectedIds: info?.selectedIds ?? [],
      names: info?.names ?? [],
      groupId: info?.groupId ?? '',
    };
  };

  const setNotificationWindowState = (
    isOpen: boolean,
    ruleId?: string,
    ruleCriteria?: RuleCriteria,
  ) => {
    const resolvedRuleCriteria =
      ruleCriteria ??
      (
        originData.value.find((item) => item.ruleId === ruleId) as
          | (MetaAlertRuleItem & { ruleCriteria?: RuleCriteria })
          | undefined
      )?.ruleCriteria;

    notificationWindowState.value = {
      isShow: isOpen,
      isLoading: isOpen,
      selectedId: ruleId ?? '',
      ruleCriteria: resolvedRuleCriteria,
    };
  };

  return {
    /* state */
    isLoading,
    ruleRows,
    checkedRows,
    originData,
    isGridReady,

    ruleWindowState,
    bulkRuleWindowState,
    copyWindowState,
    ruleMoveWindowState,
    notificationWindowState,

    setRuleWindowState,
    setBulkRuleWindowState,
    setCopyWindowState,
    setRuleMoveWindowState,
    setNotificationWindowState,

    /* actions */
    fetchRuleList,
    getRuleList,
    resetCheckedRows,
  };
});
